import React from 'react';
import { Badge } from '../../ui/badge';

/**
 * Unified mission type badge for Dispatch / Planning / Detail views.
 *
 * Accepts either the raw mission object or explicit type/subtype props.
 * Falls back to `type` / `source` / `is_shared` / `shared_trip_id` / `trip_direction`
 * for missions that were not enriched with the new backend fields yet.
 */
export function resolveMissionContext(m) {
  if (!m) return { label: '—', color: 'bg-white/10 text-white/60' };

  // Prefer explicit backend fields if available (new format)
  const backendType = m.type;
  const backendSubtype = m.subtype;
  const contextLabel = m.context_label;

  // Derive type: legacy uses uppercase 'EXCURSION' / 'VTC'; new uses 'excursion' / 'transfer'
  const isExcursion =
    backendType === 'excursion' ||
    m.type === 'EXCURSION' ||
    m.source === 'excursion_missions' ||
    m.source === 'excursion_returns';

  const isReturn =
    backendSubtype === 'return' ||
    m.trip_direction === 'retour' ||
    m.source === 'excursion_returns';

  const isShuttle =
    backendSubtype === 'shuttle' ||
    (isExcursion && !!m.shared_trip_id && !!m.is_shared);

  const isShared =
    backendSubtype === 'shared' ||
    (isExcursion && !!m.is_shared && !m.shared_trip_id);

  if (!isExcursion) {
    return { label: 'TRANSFERT', color: 'bg-blue-500/15 text-blue-400 border-blue-500/30' };
  }
  if (isShuttle) {
    return {
      label: isReturn ? 'NAVETTE RETOUR' : 'NAVETTE',
      color: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
    };
  }
  if (isShared) {
    return {
      label: isReturn ? 'PARTAGÉ RETOUR' : 'PARTAGÉ',
      color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    };
  }
  if (isReturn) {
    return {
      label: 'EXCURSION RETOUR',
      color: 'bg-violet-500/15 text-violet-400 border-violet-500/30',
    };
  }
  return {
    label: contextLabel ? contextLabel.toUpperCase() : 'EXCURSION',
    color: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
  };
}

export function MissionTypeBadge({ mission, size = 'sm', className = '' }) {
  const { label, color } = resolveMissionContext(mission);
  const sizeClass = size === 'xs' ? 'text-[9px] px-1.5 py-0' : 'text-[10px] px-2 py-0.5';
  return (
    <Badge
      data-testid="mission-type-badge"
      className={`${sizeClass} border ${color} font-medium tracking-wide ${className}`}
    >
      {label}
    </Badge>
  );
}

export default MissionTypeBadge;
